import React, { useState, useEffect, useReducer, useRef, useContext, useMemo } from "react"
import { isSameDay, parseISO, format } from "date-fns"
import { IconButton, LinearProgress } from "@material-ui/core"
import MessageOptionsMenu from "../MessageOptionsMenu"
import api from "../../services/api"
import toastError from "../../errors/toastError"
import { SocketContext } from "../../context/Socket/SocketContext"
import useStyles from "./styles"
import { conversaGrupoContatoCorGerar } from "../../utils/conversaGrupoContatoCorGerar"
import ContactModal from "../ContactModal"
import { CircleChevronDown } from "lucide-react"
import "./styles.css"
import ViewContactsModal from "../ViewContactsModal"
import { useTheme } from "@material-ui/core"
import { AuthContext } from "../../context/Auth/AuthContext"
import { EditMessageContext } from "../../context/EditMessage/EditMessageContext"
import PropTypes from "prop-types"
import useTempMessagesStore from "../../stores/tempMessage"
import { dataDescricao } from "../../utils/dataDescricao"
import { reducer } from "./hooks/reducer"
import useScrollToBottom from "./components/utils/useScrollToBottom"
import useMarkAsRead from "./hooks/useMarkAsRead"
// NOVO COMPONENTE CRIADO PARA MEMOIZAÇÃO
import MessageItem from "./components/MessageItem"
import { mensagensMidiasPrecarregar } from "./components/utils/preloadMessageMedia"
import mensagemSocketVisibilidadeAplicar from "./components/utils/mensagemSocketVisibilidadeAplicar"
import mensagemErroEntregaDerivar from "./components/utils/mensagemErroEntregaDerivar"

const N_MENSAGENS_INICIAIS_RAPIDAS = 8
const N_MENSAGENS_PAGINA_COMPLETA = 20

const MessagesList = ({ ticket, ticketId, isGroup, espiando }) => {
  const classes = useStyles()
  const theme = useTheme()

  const [messagesList, dispatch] = useReducer(reducer, [])
  const [pageNumber, setPageNumber] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(false)
  const lastMessageRef = useRef()

  const { editMessage } = useContext(EditMessageContext)
  const {
    tempMessages,
    confirmTempMessage,
    updateTempMessage,
    confirmTempMessageById,
    removeTempMessage,
    updateTempMessageById,
    resetTemp,
  } = useTempMessagesStore()

  // REMOVIDO: const [allMessages, setAllMessages] = useState([]); // Será substituído por useMemo

  const [selectedMessage, setSelectedMessage] = useState({})
  const [anchorEl, setAnchorEl] = useState(null)
  const messageOptionsMenuOpen = Boolean(anchorEl)
  const currentTicketId = useRef(ticketId)
  const { user } = useContext(AuthContext)
  const socketManager = useContext(SocketContext)
  const [newMessages, setNewMessages] = useState(0)

  // Referências e estados para controle do scroll
  const [showScrollButton, setShowScrollButton] = useState(false)
  const observerRef = useRef(null)
  const messagesEndRef = useRef(null)
  const messagesListRef = useRef(null)
  const scrollTimeoutRef = useRef(null)

  // Marcação de mensagens como lidas via HTTP (REST)
  const { marcarComoLida } = useMarkAsRead({ user, ticket, espiando })

  // Função de scroll para baixo otimizada
  const { scrollToBottom, lValidateNewMessage } = useScrollToBottom(
    messagesEndRef,
    messagesListRef,
    scrollTimeoutRef,
    setNewMessages,
    setShowScrollButton,
  )

  // Implementação de useMemo para calcular a lista ordenada de mensagens
  const orderedMessages = useMemo(() => {
    // 1. Concatena e ordena (apenas quando messagesList ou tempMessages mudam)
    const all = [...messagesList, ...tempMessages]

    return all.sort((a, b) => {
      // Prioriza 'createdAt' se existir, se não, trata como muito antigo para ordenação
      const dateA = a?.createdAt ? new Date(a.createdAt) : new Date(0)
      const dateB = b?.createdAt ? new Date(b.createdAt) : new Date(0)
      return dateA - dateB
    })
  }, [messagesList, tempMessages])

  // Efeito para monitorar o scroll e o botão de "ir para o final"
  useEffect(() => {
    if (!messagesEndRef.current || !messagesListRef.current) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Mostrar botão apenas se o marcador não estiver visível
        const isAtBottom = entry.intersectionRatio >= 0.9
        setShowScrollButton(!isAtBottom)
        if (isAtBottom) {
          setNewMessages(0)
        }
      },
      {
        root: messagesListRef.current,
        threshold: [0, 0.1, 0.9, 1], // Múltiplos thresholds para melhor detecção
        rootMargin: "0px 0px 100px 0px",
      },
    )

    observer.observe(messagesEndRef.current)
    observerRef.current = observer

    // Adicionar listener de scroll para maior precisão
    const handleScrollEvent = () => {
      // Renomeado para evitar conflito
      const { scrollTop, scrollHeight, clientHeight } = messagesListRef.current
      const isAtBottom = scrollHeight - (scrollTop + clientHeight) < 100
      setShowScrollButton(!isAtBottom)
      if (isAtBottom) {
        setNewMessages(0)
      }
    }

    messagesListRef.current.addEventListener("scroll", handleScrollEvent)

    return () => {
      observer.disconnect()
      if (messagesListRef.current) {
        messagesListRef.current.removeEventListener("scroll", handleScrollEvent)
      }
    }
  }, []) // Dependências removidas, pois a lógica de observer/scroll não muda

  // REMOVIDO: Efeito para monitorar carregamento de imagens.

  // Efeito para reset quando ticket muda
  useEffect(() => {
    dispatch({ type: "RESET" })
    setPageNumber(1)
    currentTicketId.current = ticketId
    // Limpa tempMessages ao mudar o ticketId (URL)
    resetTemp()
  }, [ticketId])

  // Efeito para carregar mensagens
  useEffect(() => {
    let lCancelado = false
    let nAgendamentoComplemento = null

    const fetchMessages = async () => {
      if (ticketId === undefined) {
        setLoading(false)
        return
      }

      setLoading(pageNumber > 1)

      try {
        const oParams = {
          pageNumber,
          espiando: espiando ? "true" : "false",
        }

        if (pageNumber === 1) {
          oParams.limite = N_MENSAGENS_INICIAIS_RAPIDAS
        }

        const { data } = await api.get("/messages/" + ticketId, {
          params: oParams,
        })

        if (!lCancelado && currentTicketId.current === ticketId) {
          mensagensMidiasPrecarregar(data.messages, { nMaximoImagens: pageNumber === 1 ? 4 : 2 })
          dispatch({ type: "LOAD_MESSAGES", payload: data.messages })
          setHasMore(data.hasMore)
          setLoading(false)
        }

        if (!lCancelado && pageNumber === 1) {
          if (data.messages.length > 1) {
            // Chamada otimizada após o carregamento da primeira página
            scrollToBottom()
          }

          if (data.messages.length > 0) {
            // Marca a conversa como lida ao abrir/carregar (via HTTP, independente do socket)
            marcarComoLida({ lForcado: true })
          }

          if (data.hasMore) {
            nAgendamentoComplemento = setTimeout(async () => {
              try {
                const { data: oComplemento } = await api.get("/messages/" + ticketId, {
                  params: {
                    pageNumber: 1,
                    limite: N_MENSAGENS_PAGINA_COMPLETA,
                    espiando: espiando ? "true" : "false",
                  },
                })

                if (!lCancelado && currentTicketId.current === ticketId) {
                  mensagensMidiasPrecarregar(oComplemento.messages, { nMaximoImagens: 2 })
                  dispatch({ type: "LOAD_MESSAGES", payload: oComplemento.messages })
                  setHasMore(oComplemento.hasMore)
                }
              } catch (_err) {
                // Complemento silencioso: a primeira página rápida já está renderizada.
              }
            }, 80)
          }
        }
      } catch (err) {
        if (!lCancelado) {
          setLoading(false)
          toastError(err)
        }
      }
    }

    fetchMessages()

    return () => {
      lCancelado = true
      if (nAgendamentoComplemento) clearTimeout(nAgendamentoComplemento)
    }
  }, [pageNumber, ticketId, espiando, marcarComoLida, scrollToBottom])

  // Socket: join + reconciliação temp + rejoin ao voltar à aba
  useEffect(() => {
    const companyId = localStorage.getItem("companyId")
    const socket = socketManager.getSocket(companyId)
    const cTicketRoom = `${ticket.id}`

    const joinChatBox = () => {
      socket.emit("joinChatBox", cTicketRoom)
    }

    socket.on("ready", joinChatBox)
    joinChatBox()

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        joinChatBox()
      }
    }
    document.addEventListener("visibilitychange", onVisibilityChange)

    socket.on(`company-${companyId}-appMessage`, (data) => {
      if (data.action === "updateAck") {
        dispatch({
          type: "UPDATE_MESSAGE",
          payload: {
            ...data.message,
            ...(data.message?.dataJson ? { dataJson: data.message.dataJson } : {}),
            ...(data.message?.erroEntregaCodigo ? { erroEntregaCodigo: data.message.erroEntregaCodigo } : {}),
          },
        })
        return
      }

      if (data.action === "create" && data.message.ticketId === currentTicketId.current) {
        const processedMessage = mensagemSocketVisibilidadeAplicar(
          data.message,
          user,
          ticket.canalTipoId,
          ticket.responsavelId ?? null
        )

        // Verifica se a mensagem existe na lista temporária
        if (confirmTempMessage(data?.mensagemIdTemporario)) {
          updateTempMessage(data?.mensagemIdTemporario, {
            ...data.message,
            isTemp: false,
          })

          dispatch({ type: "ADD_MESSAGE", payload: processedMessage })

          removeTempMessage(data?.mensagemIdTemporario)
        } else {
          // Se não era temporária (veio de outro lugar), apenas adiciona.
          dispatch({ type: "ADD_MESSAGE", payload: processedMessage })
        }

        lValidateNewMessage(data)
      }

      if (data.action === "update") {
        const nTicketIdDaMensagem = data.message?.ticketId || data.ticketId
        const lEhTicketAtual = nTicketIdDaMensagem === currentTicketId.current
        const lMensagemExisteNaLista = data.message?.id && messagesList.some((m) => m.id === data.message.id)

        if (lEhTicketAtual || lMensagemExisteNaLista) {
          const oMensagemProcessada = mensagemSocketVisibilidadeAplicar(
            data.message || data,
            user,
            ticket.canalTipoId,
            ticket.responsavelId ?? null
          )

          dispatch({ type: "UPDATE_MESSAGE", payload: oMensagemProcessada })
        }
      }
    })

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange)
      socket.emit("leaveChatBox", cTicketRoom)
      socket.off("ready", joinChatBox)
      socket.off(`company-${companyId}-appMessage`)
    }
  }, [ticketId, ticket, socketManager, user?.id])

  // Efeito para garantir o scroll no final após o cálculo de orderedMessages
  useEffect(() => {
    // Scrolla para baixo se não estivermos subindo a lista (showScrollButton é false = estamos no final)
    !showScrollButton && scrollToBottom("smooth")
  }, [orderedMessages, showScrollButton, scrollToBottom])

  const loadMore = () => {
    setPageNumber((prevPageNumber) => prevPageNumber + 1)
  }

  const handleScroll = (e) => {
    if (!hasMore) return
    const { scrollTop } = e.currentTarget

    if (scrollTop === 0) {
      e.currentTarget.scrollTop = 1
    }

    if (loading) {
      return
    }

    if (scrollTop < 50) {
      loadMore()
    }
  }

  const handleOpenMessageOptionsMenu = (e, message) => {
    if (!espiando && message.ack !== 1) {
      if (anchorEl === null) {
        e.preventDefault()
        setAnchorEl(e.currentTarget)
        setSelectedMessage(message)
      } else {
        setAnchorEl(null)
      }
    }
  }

  const handleCloseMessageOptionsMenu = () => {
    setAnchorEl(null)
  }

  // --- Funções Auxiliares de ContactModal --- (inalteradas, mas movidas para o fim)
  const [contactModalOpen, setContactModalOpen] = useState(false)
  const [viewContactsModalOpen, setViewContactsModalOpen] = useState(false)
  const [initialValues, setInitialValues] = useState({ name: "", phone: "" })
  const [contactsCards, setContactsCards] = useState([])

  const handleCloseContactModal = () => {
    setContactModalOpen(false)
  }

  const handleCloseViewContactsModal = () => {
    setViewContactsModalOpen(false)
  }

  const openViewContactsModal = (contacts) => {
    setViewContactsModalOpen(true)
    setContactsCards(contacts)
  }
  // --- FIM das Funções Auxiliares ---

  // Função principal de renderização da lista, agora usa MessageItem
  const renderMessages = (messages) => {
    return messages.map((message, index) => {
      if (!message || !message.createdAt) return null

      const previousMessage = messages[index - 1]

      const oErroEntrega = mensagemErroEntregaDerivar({
        fromMe: message.fromMe,
        dataJson: message.dataJson,
        erroEntregaCodigo: message.erroEntregaCodigo,
      })

      return (
        <MessageItem
          ticket={ticket}
          canalTipoId={ticket?.canalTipoId}
          key={message.id || message.mensagemIdTemporario}
          message={message}
          previousMessage={previousMessage}
          isGroup={isGroup}
          theme={theme}
          handleOpenMessageOptionsMenu={handleOpenMessageOptionsMenu}
          // Props para modal de contato
          setInitialValues={setInitialValues}
          setContactModalOpen={setContactModalOpen}
          openViewContactsModal={openViewContactsModal}
          // Referências e utilitários
          lastMessageRef={index === messages.length - 1 ? lastMessageRef : null}
          conversaGrupoContatoCorGerar={conversaGrupoContatoCorGerar}
          dataDescricao={dataDescricao}
          parseISO={parseISO}
          format={format}
          isSameDay={isSameDay}
          cTextoErroEntrega={oErroEntrega.exibir ? oErroEntrega.texto : null}
        />
      )
    })
  }

  return (
    <div
      className={classes.messagesListWrapper}
      style={{
        ...(editMessage
          ? {
              filter: "blur(7px)",
              pointerEvents: "none",
              userSelect: "none",
              transition: "filter 0.5s",
            }
          : {}),
      }}
    >
      <ContactModal
        open={contactModalOpen}
        onClose={handleCloseContactModal}
        aria-labelledby="form-dialog-title"
        initialValues={initialValues}
      />
      <ViewContactsModal
        open={viewContactsModalOpen}
        onClose={handleCloseViewContactsModal}
        contactsCards={contactsCards}
      />
      <MessageOptionsMenu
        message={selectedMessage}
        ticket={ticket}
        anchorEl={anchorEl}
        menuOpen={messageOptionsMenuOpen}
        handleClose={handleCloseMessageOptionsMenu}
      />
      {loading && (
        <div>
          <LinearProgress className={classes.loading} />
        </div>
      )}
      <div
        id="messagesList"
        data-testid="messages-list"
        className={classes.messagesList}
        onScroll={handleScroll}
        ref={messagesListRef}
      >
        {/* Passa orderedMessages para renderMessages */}
        {renderMessages(orderedMessages)}
        <div ref={messagesEndRef} style={{ float: "left", clear: "both", height: "1px" }} />
      </div>

      {showScrollButton && (
        <div
          style={{
            position: "absolute",
            bottom: 0,
            right: 5,
            zIndex: 1000,
          }}
        >
          <IconButton onClick={() => scrollToBottom("smooth")} className={classes.scrollButton}>
            <CircleChevronDown />
            {newMessages > 0 && <span className={classes.newMessagesCount}>{newMessages}</span>}
          </IconButton>
        </div>
      )}
    </div>
  )
}
MessagesList.propTypes = {
  ticket: PropTypes.object,
  ticketId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isGroup: PropTypes.bool,
  espiando: PropTypes.bool,
}

export default MessagesList
